import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import { qformat, DATE_FORMATS } from 'views_shared/app/javascript/utils/dateFormatters';
import { profileUrl, requestUrl, expertUrl } from 'views_shared/app/javascript/utils/urls';
import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import EntitySubtitle from 'views_shared/app/javascript/components/EntitySubtitle';
import MessageContent from '../Message/MessageContent';
import Attachment from '../Attachment';

// banner: optional slot rendered under the request-title bar (pitch inbox's flagged-user
// warning); null (the default) keeps the messaging inbox exactly as before.
function PitchAsMessage({ conversation, banner = null }) {
  const [pitch, setPitch] = useState(null);
  const [request, setRequest] = useState(null);
  const [profile, setProfile] = useState(null);
  // The "no longer accessible" fallback must only render once the fetch settles empty —
  // rendering it while the fetch is in flight flashes the warning on every conversation open.
  const [status, setStatus] = useState('loading');

  // V1 renders this too, and the entity slugs here are V2 slugs — V1's /sources pages need
  // the V1 id (= the expert's migrate_id). Resolve once per pitch; expertUrl falls back to
  // the absolute V2 page while (or if) the lookup is pending. No-op on V2 (no V2_URL).
  const [entityMigrateIds, setEntityMigrateIds] = useState({});
  useEffect(() => {
    const promoEntityIds = (pitch?.pitched_entities || []).map((e) => e.promo_entity_id).filter(Boolean);
    if (!globalThis.Qwoted?.V2_URL || !promoEntityIds.length) return;
    globalThis.Qwoted.supabaseClient
      .from('promo_entities')
      .select('id, migrate_id')
      .in('id', promoEntityIds)
      .then(({ data }) => {
        if (data) setEntityMigrateIds(Object.fromEntries(data.map((row) => [row.id, row.migrate_id])));
      });
  }, [pitch]);

  // Fetch the pitch, then its profile and related request in parallel
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setStatus('loading');
      setPitch(null);
      setProfile(null);
      setRequest(null);

      const { data: pitchData, error: pitchError } = await qapi.pitches.get(conversation.origin_id);
      if (pitchError || !pitchData?.length) {
        if (pitchError) qlog.error('Failed to fetch pitch:', pitchError);
        if (!cancelled) { setStatus('missing'); }
        return;
      }

      const fetchedPitch = pitchData[0];
      if (cancelled) return;
      setPitch(fetchedPitch);
      setStatus('loaded');

      // Profile and request are independent — fetch in parallel
      const profilePromise = qapi.userProfiles.get(fetchedPitch.profile_slug).then(({ data, error }) => {
        if (error) qlog.error('Failed to fetch profile:', error);
        else if (data?.length > 0 && !cancelled) setProfile(data[0]);
      });

      const requestPromise = fetchedPitch.request_id
        ? qapi.requests
            .get(fetchedPitch.request_id)
            .then((result) => {
              if (!cancelled) setRequest(result);
            })
            .catch((error) => qlog.error('Failed to fetch request:', error))
        : Promise.resolve();

      await Promise.all([profilePromise, requestPromise]);
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [conversation]);

  if (status === 'loading') {
    return (
      <div className="m-2 p-2 placeholder-glow" aria-hidden="true">
        <span className="placeholder col-5" />
      </div>
    );
  }

  if (!pitch) {
    return (
      <div className="alert alert-info fst-italic m-2 p-2">
        This conversation started with a pitch, which is no longer accessible.
      </div>
    );
  }

  return (
    <div className="mb-2">
      <div>
        {request && (
          <div className="d-flex align-items-center paragraph-md fw-semibold text-body mb-3 bg-neutral-10 py-3 px-4">
            <i className="fa-solid fa-megaphone me-2 text-primary-60" />
            <span className="text-truncate">{request.title}</span>
            <a
              href={requestUrl(request)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-sm ms-auto text-nowrap"
            >
              View
            </a>
          </div>
        )}
        {banner}

        <div className="mb-3 p-2">
          {profile && pitch && (
            <div className="d-inline-flex align-items-center mb-2 ms-3">
              <ImageCropped
                roundedCircle
                src={profile.profile_image_url}
                alt={profile.name}
                size={40}
                wrapperClasses="me-3"
                fallbackToInitials
              />
              <a
                className="fw-bold me-2 message-sender-name"
                href={profileUrl(profile)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {profile.name}
              </a>
              <div className="small text-secondary lh-lg">{qformat(pitch.created_at, DATE_FORMATS.time)}</div>
            </div>
          )}

          {/* Rich (html) pitches sanitize+render like messages; legacy plain stays an escaped text node. */}
          <div className="message-body">
            {/* Video-only pitches have no text (null after the V1 sync): the video below is the pitch. */}
            {pitch.pitch_text && <MessageContent body={pitch.pitch_text} contentFormat={pitch.content_format} />}
            {/* The pitch's files/videos, same renderer as message attachments. ReferencedModel
                entries are the proposed entities — rendered as chips below, not downloadable files. */}
            {(pitch.pitch_attachments || [])
              .filter((attachment) => attachment.type !== 'Interact::Attachment::ReferencedModel')
              .map((attachment) => (
                <Attachment key={attachment.id} attachment={attachment} />
              ))}

            {/* Proposed sources, per the V2 Messaging "View Pitch in Inbox" design — the
                same chip anatomy as the request page's pitch cards */}
            {(pitch.pitched_entities || []).length > 0 && (
              <div className="mt-3">
                <p className="paragraph-sm fw-bold mb-2">
                  Proposed Source{pitch.pitched_entities.length > 1 ? 's' : ''}:
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {pitch.pitched_entities.map((entity) => (
                    <div key={entity.pitched_entity_id || entity.slug} className="pam-entity-chip">
                      {entity.image_url && (
                        <ImageCropped
                          roundedCircle
                          src={entity.image_url}
                          alt={entity.name}
                          size={43}
                          fallbackToInitials
                        />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-baseline gap-1">
                          <span className="text-truncate">{entity.name}</span>
                          <a
                            href={expertUrl({
                              slug: entity.slug,
                              migrate_id: entityMigrateIds[entity.promo_entity_id],
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pam-view-profile flex-shrink-0"
                          >
                            (view profile)
                          </a>
                        </div>
                        {(entity.title || entity.company_name) && (
                          <p className="text-secondary mb-0 text-truncate">
                            <EntitySubtitle entity={entity} linkClassName="pam-company-link" />
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

PitchAsMessage.propTypes = {
  conversation: PropTypes.shape({
    origin_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    origin_type: PropTypes.string,
  }).isRequired,
  banner: PropTypes.node,
};

export default PitchAsMessage;
